import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  RadioGroup, RadioGroupItem,
} from '../components/ui/radio-group';
import { Badge } from '../components/ui/badge';
import { Signature, UploadSimple, Lock, FloppyDisk, User, Key } from '@phosphor-icons/react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 500 * 1024;

export default function ProfilePage() {
  const { user: authUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('first_last');
  const [customName, setCustomName] = useState('');
  const [signaturePreview, setSignaturePreview] = useState('');
  const [signatureChanged, setSignatureChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // ------- Change Password state -------
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdShow, setPwdShow] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/profile/me`);
      setProfile(res.data);
      setMode(res.data.display_name_mode || 'first_last');
      setCustomName(res.data.custom_display_name || '');
      setSignaturePreview(res.data.signature_data || '');
    } catch {
      toast.error('Failed to load profile');
    } finally { setLoading(false); }
  };

  const locked = !!profile?.signature_locked;

  const onFilePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error('Please pick a PNG, JPEG or WEBP image'); return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Signature must be under ${Math.round(MAX_BYTES / 1024)} KB`); return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setSignaturePreview(String(reader.result));
      setSignatureChanged(true);
    };
    reader.readAsDataURL(file);
  };

  const previewName = () => {
    const fn = (authUser?.first_name || '').trim();
    const ln = (authUser?.last_name || '').trim();
    const email = (authUser?.email || '').trim();
    if (mode === 'first' && fn) return fn;
    if (mode === 'email' && email) return email;
    if (mode === 'custom' && customName.trim()) return customName.trim();
    return (`${fn} ${ln}`.trim()) || email || 'User';
  };

  const changePassword = async () => {
    if (pwdNew.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (pwdNew !== pwdConfirm) { toast.error('New password and confirmation do not match'); return; }
    setPwdSaving(true);
    try {
      await axios.post(`${API_URL}/profile/change-password`, {
        current_password: pwdCurrent,
        new_password: pwdNew,
      });
      toast.success('Password changed. Please sign in again on other devices.');
      setPwdCurrent(''); setPwdNew(''); setPwdConfirm('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change password');
    } finally { setPwdSaving(false); }
  };


  const saveAll = async () => {
    if (locked) {
      toast.error('Locked. Ask an Admin to unlock before making changes.'); return;
    }
    // Nothing changed vs the persisted profile? Don't hit the API — avoid a
    // surprise 403 landing on the user after they land on the page and click
    // Save with no changes.
    const nameUnchanged = (
      (mode || 'first_last') === (profile.display_name_mode || 'first_last') &&
      (customName || '') === (profile.custom_display_name || '')
    );
    if (!signatureChanged && nameUnchanged) {
      toast.info('Nothing to save'); return;
    }
    setSaving(true);
    try {
      // Save display-name mode first
      await axios.put(`${API_URL}/profile/display-name`, {
        display_name_mode: mode,
        custom_display_name: customName,
      });
      // If a NEW signature was picked, upload it (this call also locks)
      if (signatureChanged && signaturePreview) {
        await axios.post(`${API_URL}/profile/signature`, { signature_data: signaturePreview });
      }
      toast.success('Profile saved. Signature & name are now locked until an Admin unlocks.');
      await fetchProfile();
      setSignatureChanged(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save profile');
    } finally { setSaving(false); }
  };

  if (loading || !profile) return (
    <div className="p-8 text-center text-muted-foreground">Loading profile…</div>
  );

  return (
    <div className="space-y-8 max-w-3xl" data-testid="profile-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold">My Profile</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Your signature and display name print on every PDF you create (invoices, quotations, POs…)
        </p>
      </motion.div>

      {/* Identity summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="card-tactile rounded-lg p-6"
      >
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-primary/10">
            <User size={26} weight="duotone" className="text-primary" />
          </div>
          <div className="flex-1">
            <div className="font-medium">{profile.first_name} {profile.last_name}</div>
            <div className="text-sm text-muted-foreground">{profile.email} · <Badge variant="outline" className="ml-1">{profile.role}</Badge></div>
          </div>
          {locked && (
            <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" data-testid="profile-locked-badge">
              <Lock size={12} /> Locked
            </Badge>
          )}
        </div>
      </motion.div>

      {/* Change Password */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
        className="card-tactile rounded-lg p-6 space-y-4"
        data-testid="change-password-card"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-medium flex items-center gap-2"><Key size={18} /> Change Password</h2>
            <p className="text-sm text-muted-foreground">Update your login password. You will stay logged in on this device.</p>
          </div>
          <button
            type="button"
            onClick={() => setPwdShow(v => !v)}
            className="text-xs text-muted-foreground hover:text-foreground underline"
            data-testid="password-show-toggle"
          >
            {pwdShow ? 'Hide passwords' : 'Show passwords'}
          </button>
        </div>
        <div className="grid gap-3 max-w-md">
          <div>
            <Label htmlFor="pwd-cur" className="text-xs">Current password</Label>
            <Input id="pwd-cur" type={pwdShow ? 'text' : 'password'} value={pwdCurrent}
                   onChange={e => setPwdCurrent(e.target.value)} autoComplete="current-password"
                   data-testid="password-current" />
          </div>
          <div>
            <Label htmlFor="pwd-new" className="text-xs">New password (min 8 chars)</Label>
            <Input id="pwd-new" type={pwdShow ? 'text' : 'password'} value={pwdNew}
                   onChange={e => setPwdNew(e.target.value)} autoComplete="new-password"
                   data-testid="password-new" />
          </div>
          <div>
            <Label htmlFor="pwd-cfm" className="text-xs">Confirm new password</Label>
            <Input id="pwd-cfm" type={pwdShow ? 'text' : 'password'} value={pwdConfirm}
                   onChange={e => setPwdConfirm(e.target.value)} autoComplete="new-password"
                   data-testid="password-confirm" />
          </div>
          <Button
            onClick={changePassword}
            disabled={pwdSaving || !pwdCurrent || pwdNew.length < 8 || pwdNew !== pwdConfirm}
            className="w-fit"
            data-testid="password-change-btn"
          >
            {pwdSaving ? 'Updating…' : 'Update password'}
          </Button>
        </div>
      </motion.div>



      {/* Display Name */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        className="card-tactile rounded-lg p-6 space-y-4"
      >
        <div>
          <h2 className="font-medium">Display Name on PDFs</h2>
          <p className="text-sm text-muted-foreground">Choose how your name appears in the "Prepared by" block.</p>
        </div>
        <RadioGroup value={mode} onValueChange={setMode} disabled={locked}>
          {[
            { v: 'first_last', l: `First + Last (e.g. ${profile.first_name} ${profile.last_name})` },
            { v: 'first', l: `First only (e.g. ${profile.first_name})` },
            { v: 'email', l: `Email (${profile.email})` },
            { v: 'custom', l: 'Custom typed name' },
          ].map(o => (
            <div key={o.v} className="flex items-center gap-2">
              <RadioGroupItem value={o.v} id={`dnm-${o.v}`} data-testid={`display-mode-${o.v}`} />
              <Label htmlFor={`dnm-${o.v}`} className="cursor-pointer">{o.l}</Label>
            </div>
          ))}
        </RadioGroup>
        {mode === 'custom' && (
          <Input
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Type the exact name to print"
            disabled={locked}
            data-testid="custom-display-name"
            className="max-w-sm"
          />
        )}
        <div className="text-xs text-muted-foreground">Preview: <span className="font-medium text-foreground">{previewName()}</span></div>
      </motion.div>

      {/* Signature Upload */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="card-tactile rounded-lg p-6 space-y-4"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-medium flex items-center gap-2"><Signature size={20} /> My Signature</h2>
            <p className="text-sm text-muted-foreground mt-1">
              PNG/JPEG/WEBP up to 500 KB. Transparent PNG works best.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="flex-shrink-0 w-56 h-32 rounded-lg border-2 border-dashed border-border bg-muted/30 flex items-center justify-center overflow-hidden">
            {signaturePreview ? (
              <img src={signaturePreview} alt="Preview" className="max-w-full max-h-full object-contain" data-testid="signature-preview" />
            ) : (
              <span className="text-xs text-muted-foreground text-center px-2">No signature yet</span>
            )}
          </div>
          <div className="space-y-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={onFilePick}
              className="hidden"
              data-testid="signature-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={locked}
              variant="outline"
              data-testid="signature-choose-btn"
            >
              <UploadSimple size={16} /> Choose signature image
            </Button>
            {locked && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <Lock size={12} /> Locked. Ask an Admin to unlock to re-upload.
              </p>
            )}
          </div>
        </div>
      </motion.div>

      {/* Save */}
      <motion.div className="flex justify-end" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        <Button
          size="lg"
          onClick={saveAll}
          disabled={locked || saving}
          data-testid="profile-save-btn"
        >
          <FloppyDisk size={18} />
          {saving ? 'Saving…' : 'Save profile'}
        </Button>
      </motion.div>
    </div>
  );
}
